import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ScrollText } from 'lucide-react';
import { auditApi } from '../lib/api-client';
import { PageHeader } from '../components/layout/PageHeader';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { LoadingState, ErrorState, EmptyState } from '../components/ui/states';

const DECISION_VARIANT = { allow: 'success', deny: 'danger', info: 'neutral' } as const;

function useAuditLog() {
  return useQuery({ queryKey: ['audit-log'], queryFn: () => auditApi.list() });
}

export function AuditLog() {
  const [search, setSearch] = useState('');
  const query = useAuditLog();

  const events = useMemo(() => {
    const list = query.data?.events ?? [];
    if (!search.trim()) return list;
    const needle = search.toLowerCase();
    return list.filter((e) => e.action.toLowerCase().includes(needle) || e.actor.toLowerCase().includes(needle) || e.resource.toLowerCase().includes(needle));
  }, [query.data, search]);

  if (query.isLoading) return <LoadingState />;
  if (query.isError) return <ErrorState error={query.error} onRetry={() => query.refetch()} />;

  return (
    <div>
      <PageHeader title="Audit Log" description="Every security-relevant decision, tamper-evident and in order." />
      <Input placeholder="Search by action, actor, or resource…" value={search} onChange={(e) => setSearch(e.target.value)} className="mb-4 max-w-md" />
      {events.length === 0 ? (
        <EmptyState icon={<ScrollText size={32} />} title="Nothing to show" description="No audit events match your search." />
      ) : (
        <div className="overflow-hidden rounded-2xl border border-border">
          <table className="w-full text-sm">
            <thead className="bg-surface-sunken text-left text-xs uppercase text-ink-muted">
              <tr>
                <th className="px-4 py-2 font-medium">When</th>
                <th className="px-4 py-2 font-medium">Action</th>
                <th className="px-4 py-2 font-medium">Actor</th>
                <th className="px-4 py-2 font-medium">Resource</th>
                <th className="px-4 py-2 font-medium">Decision</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border bg-surface-raised">
              {events
                .slice()
                .reverse()
                .map((event) => (
                  <tr key={event.seq}>
                    <td className="whitespace-nowrap px-4 py-2 text-ink-muted">{new Date(event.timestamp).toLocaleString()}</td>
                    <td className="px-4 py-2 font-mono text-xs text-ink">{event.action}</td>
                    <td className="px-4 py-2 text-ink-muted">{event.actor}</td>
                    <td className="max-w-xs truncate px-4 py-2 text-ink-muted">{event.resource}</td>
                    <td className="px-4 py-2">
                      <Badge variant={DECISION_VARIANT[event.decision]}>{event.decision}</Badge>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
